import React from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Trash2, Minus, Plus, ShoppingBag } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CartDrawer() {
  const { items, removeItem, updateQuantity, totalPrice, totalItems, isOpen, setIsOpen, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleCheckout = async () => {
    if (!user) {
      setIsOpen(false);
      navigate('/login');
      return;
    }

    try {
      const { data } = await axios.post(
        `${API}/api/shop/checkout`,
        {
          items: items.map(i => ({ product_id: i.product.id, quantity: i.quantity })),
          origin_url: window.location.origin
        },
        { withCredentials: true }
      );
      window.location.href = data.url;
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Checkout failed. Please try again.');
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent className="bg-[#0A0A0A] border-l border-white/10 w-full sm:max-w-md flex flex-col" aria-describedby="cart-description">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl text-[#F5F5F0] flex items-center gap-3">
            <ShoppingBag size={20} className="text-[#C5A059]" />
            Cart ({totalItems})
          </SheetTitle>
          <p id="cart-description" className="sr-only">Your shopping cart</p>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-[#525252] font-mono text-xs uppercase tracking-wider">Your cart is empty</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto py-4 space-y-4">
              {items.map(({ product, quantity }) => (
                <div key={product.id} className="flex gap-4 p-3 border border-white/5">
                  <img
                    src={product.image_url}
                    alt={product.title}
                    className="w-20 h-20 object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-[#F5F5F0] text-sm truncate">{product.title}</h4>
                    <p className="text-[#C5A059] font-serif text-lg">${product.price.toLocaleString()}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <button
                        onClick={() => updateQuantity(product.id, quantity - 1)}
                        className="w-6 h-6 flex items-center justify-center border border-white/20 text-[#A3A3A3] hover:text-[#F5F5F0]"
                        data-testid={`cart-minus-${product.id}`}
                      >
                        <Minus size={12} />
                      </button>
                      <span className="text-[#F5F5F0] text-sm font-mono w-6 text-center">{quantity}</span>
                      <button
                        onClick={() => updateQuantity(product.id, quantity + 1)}
                        className="w-6 h-6 flex items-center justify-center border border-white/20 text-[#A3A3A3] hover:text-[#F5F5F0]"
                        data-testid={`cart-plus-${product.id}`}
                      >
                        <Plus size={12} />
                      </button>
                      <button
                        onClick={() => removeItem(product.id)}
                        className="ml-auto text-[#D84B79] hover:text-[#F5F5F0]"
                        data-testid={`cart-remove-${product.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="border-t border-white/10 pt-4 space-y-4">
              <div className="flex justify-between">
                <span className="text-[#A3A3A3] font-mono text-xs uppercase tracking-wider">Total</span>
                <span className="text-[#C5A059] font-serif text-2xl">${totalPrice.toLocaleString()}</span>
              </div>
              <Button
                onClick={handleCheckout}
                className="w-full bg-[#C5A059] text-[#0A0A0A] hover:bg-[#E0BE77] font-mono text-xs uppercase tracking-[0.2em] py-6"
                data-testid="checkout-btn"
              >
                {user ? 'Proceed to Checkout' : 'Sign In to Checkout'}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
